import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { periodLockingService } from '../../services/financial/period-locking';
import { 
  XMarkIcon, 
  LockClosedIcon, 
  LockOpenIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

const PeriodLockingModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [periods, setPeriods] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  
  // Form states
  const [lockForm, setLockForm] = useState({
    startDate: '',
    endDate: '',
    lockType: 'soft',
    password: '',
    reason: ''
  });

  const [unlockForm, setUnlockForm] = useState({
    password: '',
    reason: ''
  });

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!currentCompany?.id) return;
      
      try {
        setLoading(true);
        const [periodsData, auditData] = await Promise.all([
          periodLockingService.getLockedPeriods(currentCompany.id),
          periodLockingService.getAuditTrail(currentCompany.id)
        ]);
        
        setPeriods(periodsData);
        setAuditTrail(auditData);
      } catch (error) {
        console.error('Error loading period locking data:', error);
        toast.error(t('period_locking.error_loading_data', 'Failed to load data'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentCompany?.id, t]);

  // Event handlers
  const handleLockPeriod = async (e) => {
    e.preventDefault();
    if (!lockForm.startDate || !lockForm.endDate || !lockForm.password) {
      toast.error(t('period_locking.fill_required_fields', 'Please fill all required fields'));
      return;
    }

    try {
      setLoading(true);
      
      // Validate period first
      const startDate = new Date(lockForm.startDate);
      const endDate = new Date(lockForm.endDate);
      
      const validation = await periodLockingService.validatePeriodClosure(
        currentCompany.id,
        startDate,
        endDate
      );
      
      if (!validation.canClose && lockForm.lockType === 'hard') {
        toast.error(t('period_locking.cannot_close_period', `Cannot close period: ${validation.issues.join(', ')}`));
        return;
      }
      
      await periodLockingService.lockPeriod(
        currentCompany.id,
        startDate,
        endDate,
        lockForm.lockType,
        user.uid,
        lockForm.reason
      );
      
      toast.success(t('period_locking.period_locked', 'Period locked successfully'));
      
      // Reset form and reload data
      setLockForm({
        startDate: '',
        endDate: '',
        lockType: 'soft',
        password: '',
        reason: ''
      });
      
      const [periodsData, auditData] = await Promise.all([
        periodLockingService.getLockedPeriods(currentCompany.id),
        periodLockingService.getAuditTrail(currentCompany.id)
      ]);
      
      setPeriods(periodsData);
      setAuditTrail(auditData);
      onSuccess?.();
    } catch (error) {
      console.error('Error locking period:', error);
      toast.error(t('period_locking.error_locking_period', 'Failed to lock period'));
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockPeriod = async (periodId) => {
    if (!unlockForm.password || !unlockForm.reason) {
      toast.error(t('period_locking.provide_password_reason', 'Please provide password and reason'));
      return;
    }

    try {
      setLoading(true);
      await periodLockingService.unlockPeriod(periodId, user.uid, unlockForm.password, unlockForm.reason);
      toast.success(t('period_locking.period_unlocked', 'Period unlocked successfully'));
      
      // Reset form
      setUnlockForm({ password: '', reason: '' });
      setSelectedPeriod(null);
      
      // Reload data
      const [periodsData, auditData] = await Promise.all([
        periodLockingService.getLockedPeriods(currentCompany.id),
        periodLockingService.getAuditTrail(currentCompany.id)
      ]);
      
      setPeriods(periodsData);
      setAuditTrail(auditData);
    } catch (error) {
      console.error('Error unlocking period:', error);
      toast.error(t('period_locking.error_unlocking_period', 'Failed to unlock period'));
    } finally {
      setLoading(false);
    }
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth();
      
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      
      options.push({ startDate, endDate, label });
    }
    
    return options;
  };

  const monthOptions = generateMonthOptions();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('period_locking.title', 'Period Locking')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <LoadingSpinner />
          </div>
        )}

        {/* Tabs */}
        <div className="border-b">
          <div className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: t('period_locking.period_overview', 'Period Overview'), icon: LockClosedIcon },
              { id: 'lock', label: t('period_locking.lock_period', 'Lock Period'), icon: ShieldCheckIcon },
              { id: 'audit', label: t('period_locking.audit_trail', 'Audit Trail'), icon: ExclamationTriangleIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Period Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium">
                {t('period_locking.locked_periods', 'Locked Periods')}
              </h3>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('period_locking.period', 'Period')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('period_locking.lock_type', 'Lock Type')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('period_locking.locked_date', 'Locked Date')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('period_locking.locked_by', 'Locked By')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('period_locking.status', t('common.status'))}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('period_locking.actions', t('common.actions'))}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {periods.map(period => (
                      <tr key={period.id}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {new Date(period.startDate.toDate()).toLocaleDateString()} - {new Date(period.endDate.toDate()).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            period.lockType === 'hard' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {period.lockType === 'hard' ? t('accounting.hard_lock') : t('accounting.soft_lock')}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {period.lockedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {period.lockedBy || 'Unknown'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            period.status === 'locked' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {period.status === 'locked' ? t('period_locking.locked', 'Locked') : t('period_locking.unlocked', 'Unlocked')}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                          {period.status === 'locked' && (
                            <button
                              onClick={() => setSelectedPeriod(period)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <LockOpenIcon className="h-4 w-4 inline mr-1" />
                              {t('period_locking.unlock', 'Unlock')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {periods.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {t('period_locking.no_locked_periods', 'No locked periods found')}
                  </div>
                )}
              </div>

              {/* Unlock Period Modal */}
              {selectedPeriod && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium">
                        {t('period_locking.unlock_period', 'Unlock Period')}
                      </h3>
                      <button
                        onClick={() => {
                          setSelectedPeriod(null);
                          setUnlockForm({ password: '', reason: '' });
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="p-3 bg-red-50 border border-red-200 rounded">
                        <p className="text-sm text-red-700">
                          {t('period_locking.unlock_warning', 'Warning: Unlocking this period will allow modifications to transactions within this period.')}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('period_locking.admin_password', 'Admin Password')} *
                        </label>
                        <input
                          type="password"
                          value={unlockForm.password}
                          onChange={(e) => setUnlockForm(prev => ({ ...prev, password: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('period_locking.unlock_reason', 'Reason for Unlocking')} *
                        </label>
                        <textarea
                          value={unlockForm.reason}
                          onChange={(e) => setUnlockForm(prev => ({ ...prev, reason: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="mt-6 flex space-x-3">
                      <button
                        onClick={() => handleUnlockPeriod(selectedPeriod.id)}
                        disabled={!unlockForm.password || !unlockForm.reason}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <LockOpenIcon className="h-4 w-4 inline mr-1" />
                        {t('period_locking.unlock_period', 'Unlock Period')}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPeriod(null);
                          setUnlockForm({ password: '', reason: '' });
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        {t('common.cancel', t('common.cancel'))}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Lock Period Tab */}
          {activeTab === 'lock' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium">
                {t('period_locking.lock_new_period', 'Lock New Period')}
              </h3>
              
              <div className="max-w-lg">
                <form onSubmit={handleLockPeriod}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('period_locking.quick_select', 'Quick Select Month')}
                      </label>
                      <select
                        onChange={(e) => {
                          const option = monthOptions[e.target.value];
                          if (option) {
                            setLockForm(prev => ({
                              ...prev,
                              startDate: option.startDate,
                              endDate: option.endDate
                            }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">{t('period_locking.select_month', 'Select a month...')}</option>
                        {monthOptions.map((option, index) => (
                          <option key={index} value={index}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('common.start_date')} *
                        </label>
                        <input
                          type="date"
                          value={lockForm.startDate}
                          onChange={(e) => setLockForm(prev => ({ ...prev, startDate: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('common.end_date')} *
                        </label>
                        <input
                          type="date"
                          value={lockForm.endDate}
                          onChange={(e) => setLockForm(prev => ({ ...prev, endDate: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('period_locking.lock_type', 'Lock Type')} *
                      </label>
                      <select
                        value={lockForm.lockType}
                        onChange={(e) => setLockForm(prev => ({ ...prev, lockType: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="soft">{t('accounting.soft_lock')} - {t('period_locking.soft_lock_desc', 'Allows authorized users to make changes')}</option>
                        <option value="hard">{t('accounting.hard_lock')} - {t('period_locking.hard_lock_desc', 'Prevents all changes')}</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('period_locking.admin_password', 'Admin Password')} *
                      </label>
                      <input
                        type="password"
                        value={lockForm.password}
                        onChange={(e) => setLockForm(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('period_locking.lock_reason', 'Reason for Locking')}
                      </label>
                      <textarea
                        value={lockForm.reason}
                        onChange={(e) => setLockForm(prev => ({ ...prev, reason: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={t('period_locking.lock_reason_placeholder', 'e.g., End of month closing, audit preparation...')}
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={!lockForm.startDate || !lockForm.endDate || !lockForm.password}
                      className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <LockClosedIcon className="h-4 w-4 inline mr-1" />
                      {t('period_locking.lock_period', 'Lock Period')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Audit Trail Tab */}
          {activeTab === 'audit' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium">
                {t('period_locking.audit_trail', 'Audit Trail')}
              </h3>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('chart.date')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('period_locking.action', 'Action')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('period_locking.period', 'Period')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.user')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('period_locking.reason', 'Reason')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {auditTrail.map(entry => (
                      <tr key={entry.id}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.timestamp?.toDate?.()?.toLocaleString() || 'N/A'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            entry.action === 'lock' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {entry.action === 'lock' ? t('period_locking.locked', 'Locked') : t('period_locking.unlocked', 'Unlocked')}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(entry.periodStart.toDate()).toLocaleDateString()} - {new Date(entry.periodEnd.toDate()).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.performedBy || 'Unknown'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {entry.reason || 'No reason provided'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {auditTrail.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {t('period_locking.no_audit_entries', 'No audit trail entries found')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PeriodLockingModal; 